import siteMessage from "@insite/client-framework/SiteMessage";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import setFieldValue from "@store/Store/Pages/CreateAccountForm/Handlers/SetFieldValue";
import useFieldValidation from "@store/Store/Pages/CreateAccountForm/Hooks/useFieldValidation";

import React, { ChangeEvent, FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const otherKey = "Other";
export const enum fields {
    label = "label",
    hintText = "hintText",
    optionValues = "optionValues",
    isRequired = "isRequired",
    labelOther = "labelOther",
    hintTextOther = "hintTextOther",
}

const enum optionValueFields {
    name = "name",
}

interface OptionValueModel {
    fields: {
        [optionValueFields.name]: string;
    };
}

export interface FieldsObj {
    [fields.label]: string;
    [fields.hintText]: string;
    [fields.optionValues]: OptionValueModel[];
    [fields.isRequired]: boolean;
}

interface OwnProps {
    fields: FieldsObj;
    valueKey: string;
    otherValueKey: string;
    value: string | undefined;
    otherValue: string | undefined;
}

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SelectWithOtherStyles {
    select?: SelectPresentationProps;
}

export const selectWithOtherStyles: SelectWithOtherStyles = {};

const styles = selectWithOtherStyles;

const SelectWithOtherOption: FC<Props> = ({ fields, valueKey, otherValueKey, value, otherValue, setFieldValue }) => {
    const { label, hintText, optionValues, isRequired } = fields;
    const [errorMessage, setErrorMessage] = useState("");
    const [errorMessageOther, setErrorMessageOther] = useState("");
    const [isOther, setIsOther] = useState(false);

    React.useEffect(() => {
        setIsOther(value === otherKey);
    }, [value]);

    const validate = () => {
        const message = isRequired && !value ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(message);
        return !message;
    };

    useFieldValidation(valueKey, value, validate);

    const changeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const field = event.target;
        setFieldValue({ key: valueKey, value: field.value });
    };

    return (
        <>
            <Select
                {...styles.select}
                label={label}
                required={isRequired}
                value={value || ""}
                error={errorMessage}
                onChange={changeHandler}
            >
                <option value="">{hintText}</option>
                {optionValues?.map(optionValue => (
                    <option key={optionValue.fields.name} value={optionValue.fields.name}>
                        {optionValue.fields.name}
                    </option>
                ))}
            </Select>
        </>
    );
};

export default connect(null, mapDispatchToProps)(SelectWithOtherOption);

export const selectWithOtherFieldDefinitions = (
    labelDefaultValue: string,
    hintDefaultValue: string,
    requiredDefaultValue: boolean,
) => {
    return [
        {
            name: fields.label,
            displayName: "Label",
            editorTemplate: "TextField",
            fieldType: "Translatable",
            defaultValue: labelDefaultValue,
            isRequired: true,
        },
        {
            name: fields.hintText,
            displayName: "Hint Text",
            editorTemplate: "TextField",
            fieldType: "Translatable",
            defaultValue: hintDefaultValue,
            isRequired: true,
        },
        {
            name: fields.isRequired,
            displayName: "Required Field",
            editorTemplate: "CheckboxField",
            defaultValue: requiredDefaultValue,
            fieldType: "General",
        },
        {
            name: fields.optionValues,
            displayName: "Options",
            editorTemplate: "ListField",
            defaultValue: [],
            getDisplay: (item: HasFields) => item.fields.name,
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: optionValueFields.name,
                    displayName: "Name",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                },
            ],
        },
    ] as FieldDefinition[];
};
